import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Package,
  Plus,
  Search,
  Edit,
  Trash2,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  X,
  Tag,
  DollarSign,
  Box,
  Loader2,
  Check,
  FolderOpen,
  MoreVertical,
  Archive,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Products = () => {
  const { isDarkMode } = useOutletContext() || {};
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  
  // Modal states
  const [showProductModal, setShowProductModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [productForm, setProductForm] = useState({
    product_name: '',
    description: '',
    price: '',
    currency: 'LRD',
    category_id: '',
    sku: '',
    inventory_quantity: 0,
    low_stock_threshold: 10
  });
  
  const [categoryForm, setCategoryForm] = useState({
    category_name: '',
    description: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        fetch(`${API_URL}/api/merchant/products${selectedCategory ? `?category_id=${selectedCategory}` : ''}${showLowStockOnly ? '&low_stock_only=true' : ''}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/merchant/categories`, { credentials: 'include' })
      ]);
      
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data);
      }
      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, showLowStockOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!productForm.product_name || !productForm.price) {
      toast.error('Product name and price are required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...productForm,
        price: parseFloat(productForm.price),
        inventory_quantity: parseInt(productForm.inventory_quantity) || 0,
        low_stock_threshold: parseInt(productForm.low_stock_threshold) || 10,
        category_id: productForm.category_id || null
      };
      
      const url = editingProduct 
        ? `${API_URL}/api/merchant/products/${editingProduct.product_id}`
        : `${API_URL}/api/merchant/products`;
      
      const res = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success(editingProduct ? 'Product updated' : 'Product created');
        setShowProductModal(false);
        resetProductForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save product');
      }
    } catch (error) {
      toast.error('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/merchant/products/${productId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Product deleted');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleToggleStatus = async (product) => {
    try {
      const newStatus = product.status === 'active' ? 'inactive' : 'active';
      const res = await fetch(`${API_URL}/api/merchant/products/${product.product_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        toast.success(`Product ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.category_name) {
      toast.error('Category name is required');
      return;
    }
    
    setSaving(true);
    try {
      const url = editingCategory
        ? `${API_URL}/api/merchant/categories/${editingCategory.category_id}`
        : `${API_URL}/api/merchant/categories`;
      
      const res = await fetch(url, {
        method: editingCategory ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(categoryForm)
      });
      
      if (res.ok) {
        toast.success(editingCategory ? 'Category updated' : 'Category created');
        setShowCategoryModal(false);
        resetCategoryForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save category');
      }
    } catch (error) {
      toast.error('Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/merchant/categories/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Category deleted');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete category');
      }
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const resetProductForm = () => {
    setProductForm({
      product_name: '',
      description: '',
      price: '',
      currency: 'LRD',
      category_id: '',
      sku: '',
      inventory_quantity: 0,
      low_stock_threshold: 10
    });
    setEditingProduct(null);
  };

  const resetCategoryForm = () => {
    setCategoryForm({ category_name: '', description: '' });
    setEditingCategory(null);
  };

  const openEditProduct = (product) => {
    setProductForm({
      product_name: product.product_name,
      description: product.description || '',
      price: product.price.toString(),
      currency: product.currency || 'LRD',
      category_id: product.category_id || '',
      sku: product.sku || '',
      inventory_quantity: product.inventory_quantity || 0,
      low_stock_threshold: product.low_stock_threshold || 10
    });
    setEditingProduct(product);
    setShowProductModal(true);
  };

  const openEditCategory = (category) => {
    setCategoryForm({
      category_name: category.category_name,
      description: category.description || ''
    });
    setEditingCategory(category);
    setShowCategoryModal(true);
  };

  const filteredProducts = products.filter(p => 
    p.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.sku && p.sku.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const isLowStock = (product) => 
    product.inventory_quantity <= product.low_stock_threshold;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="products-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className={`text-2xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
            Products
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Manage your product inventory
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => { resetCategoryForm(); setShowCategoryModal(true); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              isDarkMode ? 'bg-slate-700 text-white hover:bg-slate-600' : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
            }`}
          >
            <FolderOpen className="w-4 h-4" />
            Categories
          </button>
          <button
            onClick={() => { resetProductForm(); setShowProductModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4" />
            Add Product
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={`flex gap-4 mb-4 p-4 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
        <div className="flex-1 relative">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} />
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
              isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200 text-slate-800'
            }`}
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className={`px-4 py-2 rounded-lg border ${
            isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200 text-slate-800'
          }`}
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat.category_id} value={cat.category_id}>{cat.category_name}</option>
          ))}
        </select>
        <label className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer ${
          showLowStockOnly 
            ? 'bg-orange-100 text-orange-700 border border-orange-200' 
            : isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-slate-600'
        }`}>
          <input
            type="checkbox"
            checked={showLowStockOnly}
            onChange={(e) => setShowLowStockOnly(e.target.checked)}
            className="sr-only"
          />
          <AlertTriangle className="w-4 h-4" />
          Low Stock
        </label>
      </div>

      {/* Products Grid */}
      <div className={`flex-1 overflow-auto rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
        {filteredProducts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <Package className={`w-16 h-16 mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
            <p className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>No products found</p>
            <button
              onClick={() => { resetProductForm(); setShowProductModal(true); }}
              className="mt-4 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600"
            >
              Add Your First Product
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-4 p-4">
            {filteredProducts.map((product, index) => (
              <div
                key={product.product_id}
                className={`p-4 rounded-xl border transition-all duration-200 hover:shadow-md ${
                  product.status === 'inactive' ? 'opacity-60' : ''
                } ${
                  isDarkMode
                    ? 'bg-slate-700/50 border-slate-600 hover:bg-slate-700'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
                style={{ animationDelay: `${index * 30}ms` }}
              >
                {/* Product Header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-semibold truncate ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                      {product.product_name}
                    </h3>
                    {product.category_name && (
                      <span className={`text-xs px-2 py-0.5 rounded mt-1 inline-block ${
                        isDarkMode ? 'bg-slate-600 text-slate-300' : 'bg-gray-100 text-slate-500'
                      }`}>
                        {product.category_name}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEditProduct(product)}
                      className={`p-1.5 rounded-lg hover:bg-black/10 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteProduct(product.product_id)}
                      className="p-1.5 rounded-lg hover:bg-red-100 text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-3">
                  <p className={`text-2xl font-bold ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                    {product.currency} {product.price.toLocaleString()}
                  </p>
                  {product.sku && (
                    <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      SKU: {product.sku}
                    </p>
                  )}
                </div>

                {/* Inventory */}
                <div className={`flex items-center justify-between p-2 rounded-lg ${
                  isLowStock(product)
                    ? 'bg-orange-100 text-orange-700'
                    : isDarkMode ? 'bg-slate-600 text-slate-300' : 'bg-gray-100 text-slate-600'
                }`}>
                  <span className="text-sm flex items-center gap-1">
                    <Box className="w-4 h-4" />
                    Stock
                  </span>
                  <span className="font-semibold">
                    {product.inventory_quantity}
                    {isLowStock(product) && (
                      <AlertTriangle className="w-4 h-4 inline ml-1" />
                    )}
                  </span>
                </div>

                {/* Status Toggle */}
                <button
                  onClick={() => handleToggleStatus(product)}
                  className={`w-full mt-3 py-2 text-sm rounded-lg transition-colors ${
                    product.status === 'active'
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {product.status === 'active' ? 'Active' : 'Inactive'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`w-full max-w-lg rounded-xl shadow-2xl ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                {editingProduct ? 'Edit Product' : 'Add Product'}
              </h2>
              <button onClick={() => setShowProductModal(false)} className="p-1 rounded hover:bg-black/10">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSaveProduct} className="p-4 space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Product Name *
                </label>
                <input
                  type="text"
                  value={productForm.product_name}
                  onChange={(e) => setProductForm({ ...productForm, product_name: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-slate-800'
                  }`}
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Price *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-slate-800'
                    }`}
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Currency
                  </label>
                  <select
                    value={productForm.currency}
                    onChange={(e) => setProductForm({ ...productForm, currency: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-slate-800'
                    }`}
                  >
                    <option value="LRD">LRD</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Category
                </label>
                <select
                  value={productForm.category_id}
                  onChange={(e) => setProductForm({ ...productForm, category_id: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-slate-800'
                  }`}
                >
                  <option value="">No Category</option>
                  {categories.map(cat => (
                    <option key={cat.category_id} value={cat.category_id}>{cat.category_name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Inventory
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={productForm.inventory_quantity}
                    onChange={(e) => setProductForm({ ...productForm, inventory_quantity: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-slate-800'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Low Stock Alert
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={productForm.low_stock_threshold}
                    onChange={(e) => setProductForm({ ...productForm, low_stock_threshold: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-slate-800'
                    }`}
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  SKU (Optional)
                </label>
                <input
                  type="text"
                  value={productForm.sku}
                  onChange={(e) => setProductForm({ ...productForm, sku: e.target.value })}
                  placeholder="Auto-generated if empty"
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-slate-800'
                  }`}
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Description
                </label>
                <textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  rows={2}
                  className={`w-full px-3 py-2 rounded-lg border ${
                    isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-slate-800'
                  }`}
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowProductModal(false)}
                  className={`flex-1 py-2 rounded-lg ${
                    isDarkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-slate-700'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingProduct ? 'Update' : 'Create'} Product
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`w-full max-w-md rounded-xl shadow-2xl ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                {editingCategory ? 'Edit Category' : 'Manage Categories'}
              </h2>
              <button onClick={() => { setShowCategoryModal(false); resetCategoryForm(); }} className="p-1 rounded hover:bg-black/10">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4">
              {/* Category List */}
              {!editingCategory && categories.length > 0 && (
                <div className="mb-4 space-y-2">
                  {categories.map(cat => (
                    <div
                      key={cat.category_id}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        isDarkMode ? 'bg-slate-700' : 'bg-gray-50'
                      }`}
                    >
                      <div>
                        <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                          {cat.category_name}
                        </p>
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          {cat.product_count || 0} products
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openEditCategory(cat)}
                          className="p-1.5 rounded hover:bg-black/10"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(cat.category_id)}
                          className="p-1.5 rounded hover:bg-red-100 text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add/Edit Category Form */}
              <form onSubmit={handleSaveCategory} className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {editingCategory ? 'Edit Category Name' : 'New Category Name'}
                  </label>
                  <input
                    type="text"
                    value={categoryForm.category_name}
                    onChange={(e) => setCategoryForm({ ...categoryForm, category_name: e.target.value })}
                    placeholder="e.g., Fruits, Drinks, Electronics"
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-slate-800'
                    }`}
                    required
                  />
                </div>
                
                <div className="flex gap-3">
                  {editingCategory && (
                    <button
                      type="button"
                      onClick={resetCategoryForm}
                      className={`flex-1 py-2 rounded-lg ${
                        isDarkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-slate-700'
                      }`}
                    >
                      Cancel Edit
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    {editingCategory ? 'Update' : 'Add'} Category
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
